import type { OperatingCostCategory } from '@/types/api'
import {
  formatCategoryTotal,
  formatOperatingCostAmount,
  formatOperatingCostDateTime,
  formatOperatingCostRate,
  formatOperatingCostSplitOrigin,
} from '../lib/operating-costs'
import styles from '../CustosOperacionaisPage.module.css'
import { useSplitCommissionCostPostings } from '../hooks/useSplitCommissionCostPostings'

type SplitCommissionCostPanelProps = {
  category: OperatingCostCategory
}

export function SplitCommissionCostPanel({ category }: SplitCommissionCostPanelProps) {
  const postings = useSplitCommissionCostPostings(category.id)

  const campaigns = postings.data?.filter_options.campaigns ?? []
  const institutions = postings.data?.filter_options.institutions ?? []
  const rows = postings.data?.postings ?? []
  const meta = postings.data?.meta
  const filteredTotal = postings.data?.filtered_total ?? 0

  return (
    <article className={styles.categoryCard} aria-labelledby={`operating-cost-category-${category.id}`}>
      <header className={styles.categoryHeader}>
        <div className={styles.categoryHeading}>
          <h3 id={`operating-cost-category-${category.id}`} className={styles.categoryTitle}>
            {category.name}
          </h3>
          <span className={styles.badge}>Padrão</span>
        </div>
        <p className={styles.categoryTotal}>{formatCategoryTotal(category)}</p>
      </header>

      <p className={styles.othersCopy}>
        Cada venda paga lança automaticamente o percentual configurado em Splits Globais. A campanha
        usa o split personalizado quando existir; senão, usa o split padrão. Tipo de split mostra
        essa escolha — Padrão ou Personalizado — e não o meio de pagamento da compra.
      </p>

      <section className={styles.postingsSection} aria-labelledby={`split-postings-${category.id}`}>
        <h4 id={`split-postings-${category.id}`} className={styles.sectionTitle}>
          Lançamentos por venda
        </h4>

        <form
          className={styles.filterBar}
          onSubmit={(event) => {
            event.preventDefault()
            const { from, to } = postings.draftFilters
            if (from !== '' && to !== '' && from > to) {
              return
            }
            postings.applyFilters()
          }}
        >
          <label className={styles.field} htmlFor={`split-filter-month-${category.id}`}>
            Mês
            <input
              id={`split-filter-month-${category.id}`}
              className={styles.input}
              type="month"
              value={postings.draftFilters.month}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  month: event.target.value,
                  from: '',
                  to: '',
                })
              }
            />
          </label>
          <label className={styles.field} htmlFor={`split-filter-from-${category.id}`}>
            Data inicial
            <input
              id={`split-filter-from-${category.id}`}
              className={styles.input}
              type="date"
              value={postings.draftFilters.from}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  from: event.target.value,
                  month: '',
                })
              }
            />
          </label>
          <label className={styles.field} htmlFor={`split-filter-to-${category.id}`}>
            Data final
            <input
              id={`split-filter-to-${category.id}`}
              className={styles.input}
              type="date"
              value={postings.draftFilters.to}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  to: event.target.value,
                  month: '',
                })
              }
            />
          </label>
          <label className={styles.field} htmlFor={`split-filter-institution-${category.id}`}>
            Entidade
            <select
              id={`split-filter-institution-${category.id}`}
              className={styles.input}
              value={postings.draftFilters.institutionId}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  institutionId: event.target.value,
                })
              }
            >
              <option value="">Todas as entidades</option>
              {institutions.map((institution) => (
                <option key={institution.id} value={String(institution.id)}>
                  {institution.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field} htmlFor={`split-filter-campaign-${category.id}`}>
            Campanha
            <select
              id={`split-filter-campaign-${category.id}`}
              className={styles.input}
              value={postings.draftFilters.campaignId}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  campaignId: event.target.value,
                })
              }
            >
              <option value="">Todas as campanhas</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={String(campaign.id)}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.filterActions}>
            <button type="submit" className={styles.primaryButton} disabled={postings.isLoading}>
              Filtrar
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              disabled={postings.isLoading}
              onClick={postings.clearFilters}
            >
              Limpar
            </button>
          </div>
        </form>

        {postings.error ? (
          <p className={styles.inlineError} role="alert">
            {postings.error}
          </p>
        ) : null}

        {postings.draftFilters.from !== '' &&
        postings.draftFilters.to !== '' &&
        postings.draftFilters.from > postings.draftFilters.to ? (
          <p className={styles.inlineError} role="alert">
            A data final precisa ser igual ou posterior à data inicial.
          </p>
        ) : null}

        {postings.data !== null && !postings.error ? (
          <p className={styles.filteredTotal}>
            Total filtrado: {formatOperatingCostAmount(filteredTotal)}
          </p>
        ) : null}

        {postings.isLoading && postings.data === null ? (
          <p className={styles.empty}>Carregando lançamentos...</p>
        ) : postings.error ? null : rows.length === 0 ? (
          <p className={styles.empty}>Nenhum lançamento de split neste filtro.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Entidade</th>
                  <th scope="col">Campanha</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Tipo de split</th>
                  <th scope="col">Split</th>
                  <th scope="col" className={styles.numeric}>
                    Percentual
                  </th>
                  <th scope="col" className={styles.numeric}>
                    Valor da venda
                  </th>
                  <th scope="col" className={styles.numeric}>
                    Custo
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((posting) => (
                  <tr key={posting.id}>
                    <td>{formatOperatingCostDateTime(posting.posted_at)}</td>
                    <td>{posting.institution_name}</td>
                    <td>{posting.campaign_name}</td>
                    <td>{posting.donor_name}</td>
                    <td>{formatOperatingCostSplitOrigin(posting.split_scope)}</td>
                    <td>{posting.split_name}</td>
                    <td className={styles.numeric}>{formatOperatingCostRate(posting.split_percentage)}</td>
                    <td className={styles.numeric}>{formatOperatingCostAmount(posting.sale_amount)}</td>
                    <td className={styles.numeric}>{formatOperatingCostAmount(posting.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.last_page > 1 && !postings.error ? (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={postings.isLoading || meta.current_page <= 1}
              onClick={() => postings.goToPage(meta.current_page - 1)}
            >
              Anterior
            </button>
            <p className={styles.paginationStatus}>
              Página {meta.current_page} de {meta.last_page}
            </p>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={postings.isLoading || meta.current_page >= meta.last_page}
              onClick={() => postings.goToPage(meta.current_page + 1)}
            >
              Próxima
            </button>
          </div>
        ) : null}
      </section>
    </article>
  )
}
